import React, { FC, ReactNode } from 'react';
import './DetailsListBox.css';
import { useBreakPoint } from '@/Shared/Logic';
import { number } from 'yup';
import { ActionButton, UIButton } from '../Buttons';
import { UI_ACTION_ICONS } from '../Buttons/UIButton/UIButton';
import { UIActionIcon, UIActionType } from '@/Shared';

export interface Row{
	title? : string;
	onClick? : () => void;
	items : Item[];
}

export interface Item {
	icon? : UIActionIcon;
	label? : string;
	onClick? : () => void;
	buttons? : ReactNode[]
}

interface DetailsListBoxProps {
	data: {
		headers: string[],
		rows: Row[]
	};
	legend: string,
}


/**
 * Component: DetailsListBox
 *
 * Description:
 * Renders a responsive list/table display for property details or any
 * tabular data. On desktop, data is presented in a table with headers
 * and rows. On smaller screens, a stacked, label-value layout is used
 * for better readability.
 *
 * Key Features:
 * - Responsive design using `useBreakPoint` hook.
 * - Automatically handles empty data (`rows.length === 0`) with a
 *   user-friendly message.
 * - Multi-line ellipsis support to prevent overflow on both headers
 *   and cell content.
 *
 * Props:
 * - data: { headers: string[], rows: string[][] }
 *   Data to display in the table. `headers` define column names,
 *   `rows` contains arrays of column values.
 * - legend: string
 *   Label displayed above the table/section for context.
 *
 * Responsibilities:
 * - Decide table vs stacked layout based on breakpoint.
 * - Render headers and cells with ellipsis to prevent layout breakage.
 * - Provide fallback text when no data is available.
 *
 * Author: Samano CASTRE
 * Date: Jan 17, 2026
 */
const DetailsListBox: FC<DetailsListBoxProps> = ({ data, legend }) => {


	/**
	 * Hook to detect screen size and adjust UI accordingly.
	 * `isDesktop` controls whether to render a full table or
	 * stacked labels for mobile/tablet.
	 */
	const { isDesktop } = useBreakPoint();
	const { headers, rows } = data;

	/** Message displayed when there is no data to render */
	const noDataDisplay = "Aucune donnée à afficher.";

	const getTitle = (
		row : Row, 
		rowIndex : number,  
		item? : Item, 
		itemIndex? : number) => {

		let title = "Ligne : " + (row.title ?? rowIndex);
		if(item) {
			title += ", Colonne : " + itemIndex 
			+ ", Contenu : " 
			+ (item.label ?? (item.buttons ? item.buttons.length + " button(s)" : ""));
		}
		return title;
	}

	return (
		<fieldset className="DetailsListBox sm-scroll" data-testid="DetailsListBox">
			<legend>{legend}</legend>
			{isDesktop &&
				<table border={0}>
					<thead>
						<tr>
							{headers.map(
								(header, index) => (
									<th key={index} title={header}>
										<span className="ellipsis-3-lines">{header}</span>
									</th>
								)
							)}
						</tr>
					</thead>
					<tbody>
						{
							rows.map((row, rowIndex) =>
								<tr 
								key={rowIndex} 
								onClick={row.onClick} 
								title={getTitle(row, rowIndex)} 
								aria-label={getTitle(row, rowIndex)} 
								>
									{
										row.items.map(
											(item, itemIdex) => (
												<td 
												key={itemIdex} 
												title={getTitle(row, rowIndex, item, itemIdex)}  
												aria-label={getTitle(row, rowIndex, item, itemIdex)}
												className={item.onClick ? 'cursor-pointer' : ''}
												onClick={item.onClick}
											>
												{
												item.label && <>
													{item.icon && <span className='withIcon'>{UI_ACTION_ICONS[item.icon]}</span>}
													<span className="ellipsis-5-lines">{item.label}</span>
													</>
												}
												{ item.buttons && <div className='flex-buttons'>{item.buttons}</div> }
												</td>
											)
										)
									}
								</tr>
							)
						}
						{
							rows.length === 0 && (
								<tr>
									<td
										className='text-center'
										colSpan={headers.length}
										title={noDataDisplay}>
										<span className="ellipsis-3-lines">{noDataDisplay}</span>
									</td>
								</tr>
							)
						}

					</tbody>
				</table>}
			{
				!isDesktop && rows.map(
					(row, rowIndex) => (
						<div className="history-box" key={rowIndex}>
							{row.items.map((col, colIndex) =>
								<label className="details-page__label" key={colIndex} onClick={col.onClick}>
									<span className='bold-text'>{headers[colIndex]}: </span>
									<span className="thin-text"> {col.label}</span>
								</label>
							)}

						</div>
					)
				)
			}
			{
				!isDesktop && rows.length === 0 &&
				<div className="history-box" title={noDataDisplay}>
					<label className="details-page__label">
						<span className="ellipsis-3-lines">{noDataDisplay}</span>
					</label>
				</div>
			}

		</fieldset>
	);
}

export default DetailsListBox;